import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Playground center header: one chrome row with a leading and a trailing slot.
 *
 * The Trace / Chat / Raw mode tabs that used to sit on a second row are gone
 * (David, 2026-08-22) — the Playground is a chat surface, and the trace and raw
 * views live on the Evaluate run detail where a recorded iteration can be
 * inspected properly.
 */
interface Props {
  /** Lead client name/model — accepted for call-site compatibility, no longer rendered. */
  targetName?: string | null;
  targetModel?: string | null;
  isMultiModelLayoutMode: boolean;
  trailing?: ReactNode;
  /**
   * Optional leading control rendered at the start of the chrome row. Kept
   * generic so future leading controls (e.g. saved-view picker) can slot in.
   */
  leading?: ReactNode;
}

export function PlaygroundCenterHeaderBar({
  isMultiModelLayoutMode,
  trailing,
  leading,
}: Props) {
  return (
    <div
      className={cn(
        "@container/playground-header flex min-w-0 w-full shrink-0 flex-col border-b border-border text-xs text-muted-foreground",
        isMultiModelLayoutMode ? "bg-background" : "bg-background/50",
      )}
      data-testid="playground-main-header"
    >
      <div className="flex h-11 min-w-0 items-center gap-2 px-3 text-xs text-muted-foreground">
        {leading ? (
          <div className="flex shrink-0 items-center">{leading}</div>
        ) : null}
        {/* The lead-client chip lived here; the chat-input client picker below
            already names the client(s), so the row would say it twice (David,
            2026-08-22). The per-chat light/dark toggle went with it: the chat
            follows the app theme. */}
        <div className="min-w-0 flex-1" />
        {trailing ? (
          <div className="flex shrink-0 items-center">{trailing}</div>
        ) : null}
      </div>
    </div>
  );
}
